import { IconFileSpreadsheet } from '../../components/layout/icons';

import type { SettingsModuleDefinition, SettingsModuleSlug } from './types';
import { indianStateOptions } from './general-settings.form';

const statusDescription = 'Use the status switch to activate or deactivate this record.';
const optionList = (values: readonly string[]) => values.map((value) => ({ value, label: value }));

const genderOptions = optionList(['Male', 'Female', 'Others']);
const imageAccept = 'image/png,image/jpeg,image/webp';

export const settingsModules: readonly SettingsModuleDefinition[] = [
  {
    slug: 'general-settings',
    label: 'General Settings',
    singular: 'general settings',
    singleton: true,
    description: 'Hospital identity, logo, TDS, per-IP request limit, and gzip compression level.',
    searchPlaceholder: '',
    columns: [],
    fields: [
      { path: 'title', label: 'Title', type: 'text', section: 'Hospital', required: true },
      { path: 'email', label: 'Email', type: 'email', section: 'Hospital', required: true },
      { path: 'address', label: 'Address', type: 'text', section: 'Hospital', required: true },
      { path: 'state', label: 'State', type: 'select', section: 'Hospital', required: true, options: indianStateOptions },
      { path: 'hfId', label: 'HF ID', type: 'text', section: 'Hospital' },
      { path: 'hmisId', label: 'HMIS ID', type: 'text', section: 'Hospital' },
      { path: 'phone', label: 'Phone No.', type: 'text', section: 'Hospital', required: true },
      { path: 'tdsPercent', label: 'TDS %', type: 'number', section: 'Hospital', defaultValue: '0', min: 0, max: 100, step: 0.01 },
      { path: 'logoDataUrl', label: 'Logo', type: 'text', section: 'Logo' },
      { path: 'smallLogoDataUrl', label: 'Small Logo', type: 'text', section: 'Logo' },
      { path: 'letterHeadImageDataUrl', label: 'Letter Head Image', type: 'text', section: 'Logo' },
      { path: 'rateLimiter.maxRequests', label: 'Rate Limiter Count (per IP)', type: 'number', section: 'Runtime', defaultValue: '500', min: 1, max: 1000000 },
      { path: 'gzipCompression.level', label: 'Gzip Compression Level', type: 'number', section: 'Runtime', defaultValue: '6', min: -1, max: 9 },
    ],
  },
  {
    slug: 'users',
    label: 'Users',
    singular: 'user',
    description: `Manage employee personal, address, employment, and identification details. ${statusDescription}`,
    searchPlaceholder: 'Search employee ID, name, email, mobile, or role',
    showView: true,
    columns: [
      { label: 'Employee ID', path: 'employeeId' },
      { label: 'Name', path: 'name' },
      { label: 'Email', path: 'email' },
      { label: 'Mobile', path: 'phone' },
      { label: 'Role', path: 'role' },
    ],
    fields: [
      { path: 'role', label: 'Role', type: 'select', section: 'Personal Information', required: true, lookupResource: 'roles', lookupValue: 'label', lookupSearchableSelect: true },
      { path: 'employeeId', label: 'Employee Id', type: 'text', section: 'Personal Information', required: true },
      { path: 'salutation', label: 'Salutation', type: 'select', section: 'Personal Information', options: optionList(['Mr.', 'Mrs.', 'Ms.', 'Miss', 'Dr.', 'Prof.']) },
      { path: 'name', label: 'Name', type: 'text', section: 'Personal Information', required: true },
      { path: 'fatherName', label: 'Father Name', type: 'text', section: 'Personal Information' },
      { path: 'motherName', label: 'Mother Name', type: 'text', section: 'Personal Information' },
      { path: 'gender', label: 'Gender', type: 'select', section: 'Personal Information', required: true, options: genderOptions },
      { path: 'maritalStatus', label: 'Marital Status', type: 'select', section: 'Personal Information', options: optionList(['Single', 'Married', 'Widowed', 'Separated', 'Not Specified']) },
      { path: 'bloodGroup', label: 'Blood Group', type: 'select', section: 'Personal Information', options: optionList(['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']) },
      { path: 'dateOfBirth', label: 'Date Of Birth', type: 'date', section: 'Personal Information' },
      { path: 'dateOfJoining', label: 'Date Of Joining', type: 'date', section: 'Personal Information' },
      { path: 'phone', label: 'Phone', type: 'text', section: 'Personal Information', required: true },
      { path: 'whatsappNo', label: 'Whatsapp No', type: 'text', section: 'Personal Information' },
      { path: 'emergencyPhoneNo', label: 'Emergency Phone No.', type: 'text', section: 'Personal Information' },
      { path: 'email', label: 'Email', type: 'email', section: 'Personal Information', required: true },
      { path: 'profileImageDataUrl', label: 'Photo (512 x 512)', type: 'image', section: 'Personal Information', accept: imageAccept, help: 'PNG, JPG, JPEG, or WebP; maximum 500 KB.' },
      { path: 'currentAddress', label: 'Current Address', type: 'textarea', section: 'Address' },
      { path: 'permanentAddress', label: 'Permanent Address', type: 'textarea', section: 'Address' },
      { path: 'qualification', label: 'Qualification', type: 'text', section: 'Others' },
      { path: 'experience', label: 'Experience', type: 'text', section: 'Others' },
      { path: 'specialization', label: 'Specialization', type: 'text', section: 'Others' },
      { path: 'note', label: 'Note', type: 'text', section: 'Others' },
      { path: 'panNumber', label: 'Pan number', type: 'text', section: 'Identification Details' },
      { path: 'identificationName', label: 'Identification name', type: 'text', section: 'Identification Details' },
      { path: 'identificationNumber', label: 'Identification number', type: 'text', section: 'Identification Details' },
    ],
  },
  {
    slug: 'doctors',
    label: 'Doctors',
    singular: 'doctor',
    description: `Manage doctor identity, department, OPD fees, and commission details. ${statusDescription}`,
    searchPlaceholder: 'Search registration, doctor name, mobile, or specialization',
    showView: true,
    columns: [
      { label: 'Doctor Name', path: 'name', format: 'doctor-name' },
      { label: 'Registration No', path: 'registrationId' },
      { label: 'Doctor Type', path: 'doctorType' },
      { label: 'Department', path: 'department', format: 'reference', lookupResource: 'departments' },
      { label: 'Specialization', path: 'specialization' },
      { label: 'Mobile', path: 'phone' },
      { label: 'OPD Fees', path: 'doctorFees', format: 'currency' },
    ],
    fields: [
      { path: 'registrationId', label: 'Registration ID', type: 'text', section: 'Doctor Information', required: true },
      { path: 'department', label: 'Department', type: 'select', section: 'Doctor Information', required: true, lookupResource: 'departments' },
      { path: 'salutation', label: 'Salutation', type: 'select', section: 'Doctor Information', defaultValue: 'Dr.', options: [{ value: 'Dr.', label: 'Dr.' }] },
      { path: 'name', label: 'Name', type: 'text', section: 'Doctor Information', required: true },
      { path: 'gender', label: 'Gender', type: 'select', section: 'Doctor Information', required: true, options: genderOptions },
      { path: 'phone', label: 'Phone', type: 'text', section: 'Doctor Information', required: true },
      { path: 'address', label: 'Address', type: 'text', section: 'Doctor Information' },
      { path: 'qualification', label: 'Qualification', type: 'text', section: 'Doctor Information' },
      { path: 'experience', label: 'Work Experience', type: 'text', section: 'Doctor Information' },
      { path: 'specialization', label: 'Specialization', type: 'text', section: 'Doctor Information' },
      { path: 'doctorType', label: 'Doctor Type', type: 'select', section: 'Doctor Information', required: true, defaultValue: 'Hospital-Doctor', options: [
        { value: 'Hospital-Doctor', label: 'Hospital Doctor' },
        { value: 'Outside-Doctor', label: 'Outside Doctor' },
        { value: 'Out-Doctor', label: 'Out Doctor' },
      ] },
      { path: 'signatureDataUrl', label: 'Signature', type: 'image', section: 'Doctor Information', accept: imageAccept, help: 'PNG, JPG, JPEG, or WebP; maximum 500 KB.' },
      { path: 'chargeCategory', label: 'Charge Category', type: 'select', section: 'OPD Fees', required: true, lookupResource: 'charge-categories' },
      { path: 'chargeSubCategory', label: 'Charges Sub-Category', type: 'select', section: 'OPD Fees', required: true, lookupResource: 'charge-categories', lookupParentPath: 'chargeCategory' },
      { path: 'doctorFees', label: 'Doctor Fees', type: 'number', section: 'OPD Fees', required: true, defaultValue: '0', min: 0, step: 0.01 },
      { path: 'commissionType', label: 'Doctor Charge Type', type: 'select', section: 'Commission', options: [
        { value: 'rs', label: 'Rs' },
        { value: 'percentage', label: '%' },
      ] },
      { path: 'commissionAmount', label: 'Doctor Charge Amount', type: 'number', section: 'Commission', min: 0, step: 0.01 },
    ],
  },
  {
    slug: 'departments',
    label: 'Departments',
    singular: 'department',
    description: `Manage department names and codes. ${statusDescription}`,
    searchPlaceholder: 'Search department name or code',
    columns: [
      { label: 'Department Name', path: 'departmentName' },
      { label: 'Department Code', path: 'departmentCode' },
      { label: 'Min Age', path: 'minAge', format: 'number' },
      { label: 'Max Age', path: 'maxAge', format: 'number' },
      { label: 'Gender', path: 'genders', format: 'list' },
    ],
    fields: [
      { path: 'departmentName', label: 'Department Name', type: 'text', section: 'Department', required: true, placeholder: 'Department Name' },
      { path: 'departmentCode', label: 'Department Code', type: 'text', section: 'Department', required: true, placeholder: 'Department Code' },
      { path: 'minAge', label: 'Min Age', type: 'number', section: 'Department', placeholder: 'Enter Minimum Age', min: 0, max: 150, step: 1 },
      { path: 'maxAge', label: 'Max Age', type: 'number', section: 'Department', placeholder: 'Enter Maximum Age', min: 0, max: 150, step: 1, minimumValuePath: 'minAge' },
      { path: 'genders', label: 'Gender', type: 'checkbox-group', section: 'Department', options: optionList(['Male', 'Female']), defaultValue: [], fullWidth: true },
    ],
  },
  {
    slug: 'referral',
    label: 'Referral / Provider / Market By',
    singular: 'referrer/provider/market person',
    description: `Manage referrers, providers, and market-by people. ${statusDescription}`,
    searchPlaceholder: 'Search name, phone, or address',
    columns: [
      { label: 'Referral Name', path: 'referralName' },
      { label: 'Phone No', path: 'phoneNo' },
      { label: 'Address', path: 'address' },
      { label: 'Type', path: 'type' },
      { label: 'Refer Type', path: 'referType' },
      { label: 'Lab Commission', path: 'labPercentage', format: 'number' },
    ],
    fields: [
      { path: 'referralName', label: 'Name', type: 'text', section: 'Referral / Provider / Market By', required: true, placeholder: 'Enter Referral Name' },
      { path: 'phoneNo', label: 'Enter Phone Number', type: 'text', section: 'Referral / Provider / Market By', placeholder: 'Enter Phone Number' },
      { path: 'address', label: 'Address', type: 'text', section: 'Referral / Provider / Market By', placeholder: 'Enter Address' },
      { path: 'type', label: 'Type', type: 'select', section: 'Referral / Provider / Market By', required: true, options: [
        { value: 'referral', label: 'Referrer' },
        { value: 'provider', label: 'Provider' },
        { value: 'market_by', label: 'Market By' },
      ] },
      { path: 'referType', label: 'Reffer Type', type: 'select', section: 'Referral / Provider / Market By', options: [
        { value: 'Inside-Doctor', label: 'Hospital Doctor' },
        { value: 'Outside-Doctor', label: 'Outside Doctor' },
        { value: 'Out-Doctor', label: 'Out Doctor' },
      ] },
      { path: 'labPercentage', label: 'Lab Commission (%)', type: 'number', section: 'Commission', defaultValue: '0', min: 0, max: 100, step: 0.01 },
    ],
  },
  {
    slug: 'charges',
    label: 'Charges',
    singular: 'charge',
    editor: 'charge',
    description: `Manage charge definitions, section rates, and investigation parameters. ${statusDescription}`,
    searchPlaceholder: 'Search charge name',
    columns: [
      { label: 'Type', path: 'chargeType' },
      { label: 'Charge Name', path: 'name' },
      { label: 'Category', path: 'category', format: 'reference' },
      { label: 'Sub Category', path: 'subCategory', format: 'reference' },
    ],
    fields: [],
  },
  {
    slug: 'packages',
    label: 'Package',
    singular: 'package',
    editor: 'package',
    description: `Build a package from charge-and-rate rows. ${statusDescription}`,
    searchPlaceholder: 'Search package name',
    columns: [
      { label: 'Package Name', path: 'packageName' },
      { label: 'Charges Name', path: 'items', format: 'list' },
      { label: 'Total Amount', path: 'totalAmount', format: 'currency' },
    ],
    fields: [],
  },
  {
    slug: 'beds',
    label: 'Bed & Ward',
    singular: 'bed',
    description: `Assign beds to wards. ${statusDescription}`,
    searchPlaceholder: 'Search bed name',
    columns: [
      { label: 'Bed Name', path: 'bedName' },
      { label: 'Ward', path: 'ward', format: 'reference', lookupResource: 'wards' },
    ],
    fields: [
      { path: 'bedName', label: 'Bed Name', type: 'text', section: 'Bed', required: true },
      { path: 'ward', label: 'Ward', type: 'select', section: 'Bed', required: true, lookupResource: 'wards' },
    ],
  },
  {
    slug: 'vials',
    label: 'Vial',
    singular: 'vial',
    description: `Manage vial names. ${statusDescription}`,
    searchPlaceholder: 'Search vial name',
    columns: [{ label: 'Vial Name', path: 'vialName' }],
    fields: [{ path: 'vialName', label: 'Vial Name', type: 'text', section: 'Vial', required: true }],
  },
  {
    slug: 'diagnoses',
    label: 'Diagnoses',
    singular: 'diagnosis',
    description: `Manage diagnosis names and ICD codes. ${statusDescription}`,
    searchPlaceholder: 'Search diagnosis name or ICD code',
    columns: [
      { label: 'Diagnosis Name', path: 'diagnosisName' },
      { label: 'ICD Code', path: 'icdCode' },
    ],
    fields: [
      { path: 'diagnosisName', label: 'Diagnosis Name', type: 'text', section: 'Diagnosis', required: true },
      { path: 'icdCode', label: 'ICD Code', type: 'text', section: 'Diagnosis', required: true },
    ],
  },
  {
    slug: 'test-samples',
    label: 'Sample for a Test',
    singular: 'sample',
    description: `Manage sample names used by investigations. ${statusDescription}`,
    searchPlaceholder: 'Search sample name',
    columns: [{ label: 'Sample Name', path: 'sampleName' }],
    fields: [{ path: 'sampleName', label: 'Sample Name', type: 'text', section: 'Sample', required: true }],
  },
  {
    slug: 'tpa-management',
    label: 'TPA Management',
    singular: 'TPA',
    description: `Manage TPA contact information. ${statusDescription}`,
    searchPlaceholder: 'Search TPA name',
    columns: [
      { label: 'TPA Name', path: 'tpaName' },
      { label: 'Contact Person Name', path: 'contactPersonName' },
      { label: 'Contact Person Phone No.', path: 'contactPersonPhone' },
    ],
    fields: [
      { path: 'tpaName', label: 'TPA Name', type: 'text', section: 'TPA', required: true },
      { path: 'contactPersonName', label: 'Contact Person Name', type: 'text', section: 'TPA', required: true },
      { path: 'contactPersonPhone', label: 'Contact Person Phone No.', type: 'text', section: 'TPA', required: true },
    ],
  },
  {
    slug: 'procedure-types',
    label: 'Procedure Type',
    singular: 'procedure type',
    description: `Manage procedure packages, eligibility, duration, and departments. ${statusDescription}`,
    searchPlaceholder: 'Search package name',
    columns: [
      { label: 'Package Name', path: 'procedureName' },
      { label: 'Duration (mins)', path: 'durationMinutes', format: 'number' },
      { label: 'Type', path: 'type' },
      { label: 'Gender', path: 'gender' },
      { label: 'Department', path: 'department', format: 'reference', lookupResource: 'departments' },
    ],
    fields: [
      { path: 'procedureName', label: 'Enter Package name', type: 'text', section: 'Procedure Type', required: true, placeholder: 'Enter Package Name' },
      { path: 'durationMinutes', label: 'Duration (in mins)', type: 'number', section: 'Procedure Type', required: true, placeholder: 'Enter Duration', min: 1, max: 1440, step: 1 },
      { path: 'type', label: 'Type', type: 'select', section: 'Procedure Type', required: true, placeholder: 'Select..', options: optionList(['Major', 'Minor']) },
      { path: 'gender', label: 'Gender', type: 'select', section: 'Procedure Type', required: true, defaultValue: 'Both', options: optionList(['Both', 'Male', 'Female']) },
      { path: 'minAge', label: 'Min Age', type: 'number', section: 'Procedure Type', placeholder: 'Enter Minimum Age', min: 0, max: 150, step: 1 },
      { path: 'maxAge', label: 'Max Age', type: 'number', section: 'Procedure Type', placeholder: 'Enter Maximum Age', min: 0, max: 150, step: 1, minimumValuePath: 'minAge' },
      { path: 'department', label: 'Department', type: 'select', section: 'Procedure Type', required: true, placeholder: 'Select Department', lookupResource: 'departments', lookupSearchable: false, fullWidth: true },
      { path: 'requiredTests', label: 'Required Tests (Optional)', type: 'multi-select', section: 'Procedure Type', lookupResource: 'charges', placeholder: 'Search and select required tests', fullWidth: true },
    ],
  },
  {
    slug: 'notices',
    label: 'Notices',
    singular: 'notice',
    description: `Manage hospital notices and priority levels. ${statusDescription}`,
    searchPlaceholder: 'Search notice content',
    columns: [
      { label: 'Notice Content', path: 'notice' },
      { label: 'Details', path: 'priorityLevel' },
    ],
    fields: [
      { path: 'notice', label: 'Enter Notice', type: 'textarea', section: 'Notice', required: true, fullWidth: true },
      { path: 'priorityLevel', label: 'Priority Level', type: 'select', section: 'Notice', required: true, options: [
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
      ] },
    ],
  },
  {
    slug: 'dialysis-machines',
    label: 'Dialysis Machine',
    singular: 'dialysis machine',
    description: `Manage dialysis machine names. ${statusDescription}`,
    searchPlaceholder: 'Search machine name',
    columns: [{ label: 'Machine Name', path: 'machineName' }],
    fields: [{ path: 'machineName', label: 'Machine Name', type: 'text', section: 'Dialysis Machine', required: true }],
  },
];

export const auxiliarySettingsModules: readonly SettingsModuleDefinition[] = [
  {
    slug: 'roles',
    label: 'Roles',
    singular: 'role',
    description: `Manage staff roles. ${statusDescription}`,
    searchPlaceholder: 'Search role name',
    columns: [{ label: 'Role Name', path: 'name' }, { label: 'Permissions', path: 'allPermissions' }],
    fields: [
      { path: 'name', label: 'Role Name', type: 'text', section: 'Role', required: true },
      { path: 'permissionIds', label: 'Assign Permissions', type: 'multi-select', section: 'Permissions', lookupResource: 'permissions', fullWidth: true },
    ],
  },
  {
    slug: 'charge-categories',
    label: 'Charges Category',
    singular: 'charge category',
    description: `Manage charge category hierarchy. ${statusDescription}`,
    searchPlaceholder: 'Search category name',
    columns: [
      { label: 'Name', path: 'name' },
      { label: 'Description', path: 'description' },
      { label: 'Parent', path: 'parentCategory', format: 'reference', lookupResource: 'charge-categories' },
    ],
    fields: [
      { path: 'name', label: 'Name', type: 'text', section: 'Category', required: true },
      { path: 'description', label: 'Description', type: 'text', section: 'Category', required: true, fullWidth: true },
      { path: 'parentCategory', label: 'Parent Category', type: 'select', section: 'Category', lookupResource: 'charge-categories' },
    ],
  },
  {
    slug: 'charge-sections',
    label: 'Charges Section',
    singular: 'charge section',
    description: `Manage billable charge sections. ${statusDescription}`,
    searchPlaceholder: 'Search charge section name',
    columns: [{ label: 'Charge Section Name', path: 'name' }],
    fields: [{ path: 'name', label: 'Charge Section Name', type: 'text', section: 'Charge Section', required: true }],
  },
  /* Temporarily hidden; retain the module definition for future reactivation.
  {
    slug: 'requisition-sections',
    label: 'Requisition Section',
    singular: 'requisition section',
    description: `Manage requisition sections. ${statusDescription}`,
    searchPlaceholder: 'Search requisition section name',
    columns: [{ label: 'Requisition Section Name', path: 'name' }],
    fields: [{ path: 'name', label: 'Requisition Section Name', type: 'text', section: 'Requisition Section', required: true }],
  },
  */
  {
    slug: 'wards',
    label: 'Ward',
    singular: 'ward',
    description: `Manage ward names. ${statusDescription}`,
    searchPlaceholder: 'Search ward name',
    columns: [{ label: 'Ward Name', path: 'wardName' }],
    fields: [{ path: 'wardName', label: 'Ward Name', type: 'text', section: 'Ward', required: true }],
  },
];

export const settingsNavigationLinks = [
  { label: 'Audit Logs', icon: IconFileSpreadsheet, to: '/audit-logs' },
] as const;

export const allSettingsModules = [...settingsModules, ...auxiliarySettingsModules] as const;

const permissionsSettingsModule: SettingsModuleDefinition = {
  slug: 'permissions',
  label: 'Permissions',
  singular: 'permission',
  description: 'Permission names available for future user and role assignment. No permissions are assigned yet.',
  searchPlaceholder: 'Search permission name',
  columns: [
    { label: 'Permission Name', path: 'name' },
  ],
  fields: [
    { path: 'name', label: 'Permission Name', type: 'text', section: 'Permission', required: true },
  ],
};

export const settingsModuleMap = Object.fromEntries(
  [...allSettingsModules, permissionsSettingsModule].map((module) => [module.slug, module]),
) as Record<SettingsModuleSlug, SettingsModuleDefinition>;

export const settingsModuleTabs = [
  {
    slugs: [
      'charges',
      'charge-categories',
      'charge-sections',
      // 'requisition-sections', // Temporarily hidden.
    ] as const,
    tabs: [
      { slug: 'charges', label: 'Charges' },
      { slug: 'charge-categories', label: 'Charges Category' },
      { slug: 'charge-sections', label: 'Charges Section' },
      // { slug: 'requisition-sections', label: 'Requisition Section' }, // Temporarily hidden.
    ],
  },
  {
    slugs: ['wards', 'beds'] as const,
    tabs: [
      { slug: 'wards', label: 'Ward' },
      { slug: 'beds', label: 'Bed' },
    ],
  },
] as const;

export const usersSettingsTabs = [
  { key: 'users', label: 'Users' },
  { key: 'roles', label: 'Roles' },
  { key: 'permissions', label: 'Permissions' },
] as const;

export const isSettingsModuleSlug = (value: string | undefined): value is SettingsModuleSlug =>
  Boolean(value && value in settingsModuleMap);
